import {
  SerpCategoryTree,
  SerpCategoryTreeNode,
} from "../../../../../contents/categories/types"
import { ProductCategoryMappingsDict } from "../../../../../types/serp"
import { sumValues } from "../../../../../utils/array"
import { SearchState } from "../../../types"

export interface CategoriesTreeItem {
  checked: boolean
  allEnabledChildrenChecked: boolean
  label: string
  code: string
  codes: string[]
  allCodes: string[]
  enabled: boolean
  count: number
  originalCount: number
  children: CategoriesTreeItem[]
  parentCode?: string
}

export interface CategoriesTree {
  root: CategoriesTreeItem[]
}

export const getCategoryLabelOverride = (
  customCategories: ProductCategoryMappingsDict | undefined,
  categoryCode: string | undefined
) =>
  customCategories && categoryCode
    ? customCategories[categoryCode.toLowerCase()]?.label
    : undefined

const getCount = (nodeAlgoliaItem: any, children: CategoriesTreeItem[]) =>
  (nodeAlgoliaItem ?? 0) + sumValues(children.map((x) => x.count))

const getOriginalCount = (
  nodeAlgoliaItem: any,
  children: CategoriesTreeItem[]
) => (nodeAlgoliaItem ?? 0) + sumValues(children.map((x) => x.originalCount))

const isChecked = (
  categoryCode: string,
  searchParams: SearchState,
  children: CategoriesTreeItem[]
) => {
  // if (children.length > 0) {
  //   return !children.some((x) => !x.checked)
  // }
  return searchParams.categoryCodes?.includes(categoryCode) ?? false
}

const getLevelCodes = (
  nodeAlgoliaItemCode: any,
  children: CategoriesTreeItem[]
): any[] => {
  const items: any[] = []
  if (nodeAlgoliaItemCode) {
    items.push(nodeAlgoliaItemCode)
  }
  for (const child of children) {
    items.push(...child.codes)
  }
  return items
}

const getLevelAllCodes = (
  node: SerpCategoryTreeNode,
  children: CategoriesTreeItem[]
): any[] => {
  const items: any[] = []
  items.push(node.code)
  for (const child of children) {
    items.push(...child.allCodes)
  }
  return items
}

const getSelectedChildren = (option: CategoriesTreeItem) => {
  const options: CategoriesTreeItem[] = []
  if (option.checked) {
    options.push(option)
  }

  for (const children of option.children ?? []) {
    options.push(...getSelectedChildren(children))
  }

  return options
}

// const hasEnabledChildren = (option: CategoriesTreeItem) =>
//   option.children.some((x) => x.count > 0)

// const allEnabledChildrenSelected = (option: CategoriesTreeItem) => {
//   const selectedChildren = getSelectedChildren(option)?.map((x) => x.code)
//   return elementsEquals(option.codes, selectedChildren)
// }

const isNodeActive = (node: CategoriesTreeItem) => {
  if (!node.allCodes.includes(node.code)) {
    return node.allEnabledChildrenChecked
  }

  return node.checked || node.allEnabledChildrenChecked
}

const allNodeChildrenSelected = (nodes: CategoriesTreeItem[]) => {
  const enabledNodes = nodes.filter((x) => x.enabled)
  return enabledNodes.length > 0 && enabledNodes.every((x) => isNodeActive(x))
}

const buildTreeLevel = (
  searchCategories: any,
  initialCategories: any,
  facetCategories: any,
  searchParams: SearchState,
  node: SerpCategoryTreeNode,
  customCategories: ProductCategoryMappingsDict | undefined,
  parent?: SerpCategoryTreeNode
): CategoriesTreeItem => {
  const initialFacetItem = initialCategories?.[node.code]
  // const facetItem = searchCategories?.[node.code]
  const filteredFacetItem =
    (facetCategories ?? searchCategories)?.[node.code] ?? 0
  const children = node.children
    .map((x) =>
      buildTreeLevel(
        searchCategories,
        initialCategories,
        facetCategories,
        searchParams,
        x,
        customCategories,
        node
      )
    )
    .filter((x) => x.originalCount > 0)

  const count = getCount(filteredFacetItem, children)

  return {
    checked: isChecked(node.code, searchParams, children),
    allEnabledChildrenChecked: count > 0 && allNodeChildrenSelected(children),
    children,
    code: node.code,
    codes: getLevelCodes(filteredFacetItem ? node.code : undefined, children),
    allCodes: getLevelAllCodes(node, children),
    label: getCategoryLabelOverride(customCategories, node.code) ?? node.name,
    count,
    enabled: count > 0,
    originalCount: getOriginalCount(initialFacetItem, children),
    parentCode: parent?.code,
  }
}

const buildTree = (
  categories: SerpCategoryTree,
  searchResponses: any[],
  searchParams: SearchState,
  customCategories: ProductCategoryMappingsDict | undefined
): CategoriesTree => {
  const searchCategories = searchResponses[0]?.facets?.categoryCode
  const initialCategories = searchResponses[1]?.facets?.categoryCode
  const facetCategories = searchResponses.find(
    (x: any) =>
      x.facets && Object.keys(x.facets).length === 1 && !!x.facets.categoryCode
  )?.facets?.categoryCode
  return {
    root: categories.root
      .map((x) =>
        buildTreeLevel(
          searchCategories,
          initialCategories,
          facetCategories,
          searchParams,
          x,
          customCategories
        )
      )
      .filter((x) => x.originalCount > 0),
  }
}

const normalizeTree = (tree: CategoriesTree): CategoriesTree => {
  if (tree.root.length === 1) {
    return normalizeTree({
      root: tree.root[0].children,
    })
  }

  return tree
}

export const buildCategoriesTree = (
  categories: SerpCategoryTree,
  searchResponses: any[],
  searchParams: SearchState,
  customCategories: ProductCategoryMappingsDict | undefined
): CategoriesTree => {
  const tree = buildTree(
    categories,
    searchResponses,
    searchParams,
    customCategories
  )
  return normalizeTree(tree)
}
